import { useSkillSuggestionPreviewBatchId } from "@app/components/assistant/details/SuggestionPreviewContext";
import { trackSuggestionPreviewEdit } from "@app/components/markdown/suggestion/suggestionTracking";
import {
  trackManageItemAction,
  useManageTracking,
} from "@app/components/pages/builder/manageTracking";
import { ArchiveSkillDialog } from "@app/components/skills/ArchiveSkillDialog";
import { SkillFavoriteButton } from "@app/components/skills/SkillFavoriteButton";
import config from "@app/lib/api/config";
import {
  getConversationRoute,
  getManageSkillsRoute,
  getSkillBuilderRoute,
} from "@app/lib/utils/router";
import type { GetSkillWithRelationsResponseBody } from "@app/types/api/skills";
import type { WorkspaceType } from "@app/types/user";
import {
  Button,
  Clipboard,
  ClipboardCheck,
  DotsHorizontal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Edit04,
  MessagePlusCircle,
  Trash01,
  useCopyToClipboard,
} from "@dust-tt/sparkle";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";

interface SkillDetailsButtonBarProps {
  skill: GetSkillWithRelationsResponseBody["skill"];
  owner: WorkspaceType;
  onClose: () => void;
  replaceOnEdit?: boolean;
  onFavoriteChange?: (
    skill: GetSkillWithRelationsResponseBody["skill"],
    isFavorite: boolean
  ) => Promise<void>;
}

export function SkillDetailsButtonBar({
  skill,
  owner,
  onClose,
  replaceOnEdit,
  onFavoriteChange,
}: SkillDetailsButtonBarProps) {
  const { t } = useLingui();
  const tracking = useManageTracking();
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [isSkillLinkCopied, copySkillLink] = useCopyToClipboard();
  const previewBatchId = useSkillSuggestionPreviewBatchId();

  // The API redacts the private fields of the skills an admin cannot read (built on spaces they
  // are not a member of) and flags it with `canRead: false`; only admins ever get such a skill.
  // Trying, favoriting or editing it would fail or work on an empty skill, so those entry points
  // are hidden. Archiving stays: it only needs the admin role.
  const isRedactedForAdmin = !skill.canRead;

  return (
    <>
      <ArchiveSkillDialog
        owner={owner}
        isOpen={showArchiveDialog}
        skill={skill}
        onClose={() => {
          setShowArchiveDialog(false);
          onClose();
        }}
      />
      <div className="flex flex-row items-center gap-2 px-1.5">
        {onFavoriteChange && !isRedactedForAdmin && (
          <SkillFavoriteButton
            isFavorite={skill.isFavorite}
            variant="outline"
            onFavoriteChange={(isFavorite) =>
              onFavoriteChange(skill, isFavorite)
            }
          />
        )}
        {!isRedactedForAdmin && (
          <Button
            size="sm"
            tooltip={t`Try skill`}
            onClick={() => trackManageItemAction(tracking, "try", skill.sId)}
            href={getConversationRoute(owner.sId, "new", `skill=${skill.sId}`)}
            variant="outline"
            icon={MessagePlusCircle}
          />
        )}
        {skill.canAdministrate && !isRedactedForAdmin && (
          <Button
            size="sm"
            tooltip={t`Edit skill`}
            href={getSkillBuilderRoute(owner.sId, skill.sId)}
            onClick={() => {
              trackManageItemAction(tracking, "edit", skill.sId);
              if (previewBatchId) {
                trackSuggestionPreviewEdit({
                  batchId: previewBatchId,
                  targetKind: "skill",
                  targetId: skill.sId,
                });
              }
            }}
            replace={replaceOnEdit}
            variant="outline"
            icon={Edit04}
          />
        )}
        <Button
          size="sm"
          tooltip={isSkillLinkCopied ? t`Copied!` : t`Copy link`}
          variant="outline"
          icon={isSkillLinkCopied ? ClipboardCheck : Clipboard}
          onClick={(e) => {
            e.stopPropagation();
            void copySkillLink(
              `${config.getAppUrl()}${getManageSkillsRoute(owner.sId, skill.sId)}`
            );
          }}
        />
        {skill.canAdministrate && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                icon={DotsHorizontal}
                size="sm"
                variant="ghost"
                tooltip={t`Skill options`}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                label={t`Archive`}
                icon={Trash01}
                variant="warning"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowArchiveDialog(true);
                }}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </>
  );
}
